import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  Category,
  CategoryList,
  CategorySelect,
  CreateCategoryBody,
  UpdateCategoryBody,
} from '@/types/backend'

/**Query Keys */
export const catQK = {
  list: (q?: { search?: string; page?: number; pageSize?: number }) =>
    ['categories', q ?? {}] as const,
  one: (id: string) => ['categories', id] as const,
  select: ['categories', 'select'] as const,
}

/**LIST (paginée) */
export const useCategoriesQuery = (
  q: { search?: string; page?: number; pageSize?: number } = {},
  enabled = true
) =>
  useQuery({
    queryKey: catQK.list(q),
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<CategoryList>(`categories`, {
        params: q,
      })
      return data
    },
  })

/**LIST (sélecteur mini) */
export const useCategorySelectQuery = (enabled = true) =>
  useQuery({
    queryKey: catQK.select,
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<CategorySelect[]>(
        `/categories/_select`
      )
      return data
    },
    staleTime: 5 * 60 * 1000,
  })

/**DETAILS */
export const useCategoryQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: catQK.one(id),
    enabled: Boolean(id) && enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<Category>(`/categories/${id}`)
      return data
    },
  })

/**CREATE */
export const useCreateCategoryMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateCategoryBody) => {
      const { data } = await apiClient.post<Category>('/categories', payload)
      return data
    },
    onSuccess: (created) => {
      // Réactualiser listes & sélecteur, seed le cache du détail
      qc.invalidateQueries({ queryKey: ['categories'] })
      qc.invalidateQueries({ queryKey: catQK.select })
      qc.setQueryData(catQK.one(created.id), created)
    },
  })
}

/**UPDATE */
export const useUpdateCategoryMutation = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateCategoryBody) => {
      const { data } = await apiClient.patch<Category>(
        `/categories/${id}`,
        payload
      )
      return data
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: catQK.list({}) })
      qc.invalidateQueries({ queryKey: catQK.select })
      qc.setQueryData(catQK.one(id), updated)
    },
  })
}

/**DELETE */
export const useDeleteCategoryMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; detach?: boolean }) => {
      const { id, detach = false } = vars
      const { data } = await apiClient.delete<Category>(`/categories/${id}`, {
        params: { detach: detach ? '1' : '0' },
      })

      return data
    },
    onSuccess: (_deleted, vars) => {
      qc.invalidateQueries({ queryKey: ['categories'] })
      qc.invalidateQueries({ queryKey: catQK.select })
      qc.removeQueries({ queryKey: catQK.one(vars.id) })
      // Optionnel: invalider les items si tu affiches les noms de catégories dans la liste
      qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}
